import toast from 'react-hot-toast'
import { Expose, Transform, Type } from 'class-transformer'
import { AxiosError } from 'axios'
import { Truck } from 'react-feather'
import moment from 'moment'

import { skuit } from 'src/services/skuit'
import { ActionDirection, WorkflowSequence } from 'src/modules/supply_chain/components/Workflow'

import { Base } from './Base'
import { Facility } from './Facility'
import { Sku } from './Sku'

enum WorkflowStatus {
  created = 'Created',
  started = 'Started',
  completed = 'Completed',
  reconciled = 'Reconciled',
  processing = 'Processing',
}

export enum AuditStatus {
  unstarted = 'us',
  generating = 'ge',
  error = 'er',
  inProgress = 'ip',
  completed = 'cm',
  reconciled = 're',
}

export enum AuditType {
  pl = 'Place Audit',
  sk = 'SKU Cycle Count',
}

export class Audit extends Base {
  static Status = AuditStatus
  static Type = AuditType
  static type = 'audit'
  static typeDisplay = 'Audit'
  static icon = Truck

  type = Audit.type
  typeDisplay = Audit.typeDisplay
  icon = Audit.icon

  guid: string = ''
  name: string = ''
  notes: string = ''

  @Type(() => Facility)
  facility?: Facility

  @Type(() => Sku)
  sku?: Sku

  @Expose({ name: 'audit_type' })
  auditType: string = 'pl'

  @Expose({ name: 'accuracy_score' })
  accuracyScore?: number

  @Expose({ name: 'error_message' })
  errorMessage?: string

  @Expose({ name: 'started_on' })
  @Transform((value) => (value ? moment(value) : value))
  startedOn?: moment.Moment

  @Expose({ name: 'completed_on' })
  @Transform((value) => (value ? moment(value) : value))
  completedOn?: moment.Moment

  @Expose({ name: 'reconciled_on' })
  @Transform((value) => (value ? moment(value) : value))
  reconciledOn?: moment.Moment

  status: AuditStatus = AuditStatus.unstarted

  static statusDisplayMap = {
    [AuditStatus.unstarted]: 'Unstarted',
    [AuditStatus.generating]: 'Processing',
    [AuditStatus.error]: 'Error',
    [AuditStatus.inProgress]: 'In Progress',
    [AuditStatus.completed]: 'Completed',
    [AuditStatus.reconciled]: 'Reconciled',
  }

  static colorStatusMap = {
    [AuditStatus.unstarted]: 'info',
    [AuditStatus.generating]: 'warning',
    [AuditStatus.error]: 'error',
    [AuditStatus.inProgress]: 'info',
    [AuditStatus.completed]: 'success',
    [AuditStatus.reconciled]: 'success',
  }

  get errorDisplay() {
    if (this.status === AuditStatus.error) {
      return this.errorMessage || 'Unknown Error'
    }
    return '-'
  }

  get statusDisplay() {
    return Audit.statusDisplayMap[this.status]
  }

  get workflowStatus() {
    if (this.status === AuditStatus.generating || this.status === AuditStatus.error) {
      return WorkflowStatus.processing
    }
    if (!this.startedOn) {
      return WorkflowStatus.created
    }
    if (!this.completedOn) {
      return WorkflowStatus.started
    }
    if (!this.reconciledOn) {
      return WorkflowStatus.completed
    }
    return WorkflowStatus.reconciled
  }

  get workflow(): WorkflowSequence {
    return [
      [WorkflowStatus.created, this.createdOn],
      [WorkflowStatus.started, this.startedOn],
      [WorkflowStatus.completed, this.completedOn],
      [WorkflowStatus.reconciled, this.reconciledOn],
    ]
  }

  getWorkflowActions = (onAction?: () => void): { [status: string]: ActionDirection } => {
    return {
      [WorkflowStatus.created]: {
        forward: [
          {
            label: 'Start',
            onClick: ({ complete }) => {
              skuit.sc.audits
                .action(this.guid, 'start')
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  toast.error(`Failed to start. "${error.response?.data}"`)
                )
                .finally(() => complete())
            },
          },
        ],
      },
      [WorkflowStatus.started]: {
        forward: [
          {
            label: 'Open Audit',
            onClick: () => {
              window.dispatchEvent(
                new CustomEvent('skuitCommand', {
                  detail: { command: 'scan', data: { scanned: this } },
                })
              )
              window.dispatchEvent(
                new CustomEvent('skuitCommand', {
                  detail: { command: 'audit' },
                })
              )
            },
          },
          {
            label: 'Complete',
            onClick: ({ complete }) => {
              skuit.sc.audits
                .action(this.guid, 'complete')
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  toast.error(`Failed to complete. "${error.response?.data}"`)
                )
                .finally(() => complete())
            },
          },
        ],
        backward: [
          {
            label: 'Undo Start',
            onClick: ({ complete }) => {
              skuit.sc.audits
                .action(this.guid, 'unstart')
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  toast.error(`Failed to undo start. "${error.response?.data}"`)
                )
                .finally(() => complete())
            },
          },
        ],
      },
      [WorkflowStatus.completed]: {
        forward: [
          {
            label: 'Reconcile',
            onClick: ({ complete }) => {
              skuit.sc.audits
                .action(this.guid, 'reconcile')
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  toast.error(`Failed to undo start. "${error.response?.data}"`)
                )
                .finally(() => complete())
            },
          },
        ],
        backward: [
          {
            label: 'Undo Complete',
            onClick: ({ complete }) => {
              skuit.sc.audits
                .action(this.guid, 'uncomplete')
                .then(() => onAction?.())
                .catch((error: AxiosError) =>
                  toast.error(`Failed to undo complete. "${error.response?.data}"`)
                )
                .finally(() => complete())
            },
          },
        ],
      },
    }
  }
}
